import React, { useEffect, useState } from 'react';
import { Link, Route, BrowserRouter as Router, Routes, useLocation, useParams } from 'react-router-dom';
import BuildingDetailPage from './components/BuildingDetailPage';
import CalculationVerificationPage from './components/CalculationVerificationPage';
import ChineseBuildingOwners from './components/ChineseBuildingOwners';
import CompanyManagement from './components/CompanyManagement';
import ConnectionHub from './components/ConnectionHub';
import ContractorPartnerships from './components/ContractorPartnerships';
import CTORecruitment from './components/CTORecruitment';
import LenderPartnerships from './components/LenderPartnerships';
import NonCompliantBuildingsDirectory from './components/NonCompliantBuildingsDirectory';
import Pitch from './components/Pitch';
import RealEstatePartnerships from './components/RealEstatePartnerships';
import SimpleBuildingView from './components/SimpleBuildingView';
import StaffHiring from './components/StaffHiring';
import TargetPropertiesAnalysis from './components/TargetPropertiesAnalysis';
// New Website Components
import BuildingOwnersLanding from './components/BuildingOwnersLanding';
import ContactUs from './components/ContactUs';
import ContractorsLanding from './components/ContractorsLanding';
import Homepage from './components/Homepage';
import LendersLanding from './components/LendersLanding';
import ToolsPage from './components/ToolsPage';
// Hidden components being added to routing
import ContractorPartnershipPage from './components/ContractorPartnershipPage';
import DefinitionsAndTAM from './components/DefinitionsAndTAM';
import EconomicOptimizer from './components/EconomicOptimizer';
import InternalRetrofitCalculator from './components/InternalRetrofitCalculator';
import LenderPartnershipPage from './components/LenderPartnershipPage';
import LL97Calculator from './components/LL97Calculator';
import PublicRetrofitCalculator from './components/PublicRetrofitCalculator';
import TenantOutreachPage from './components/TenantOutreachPage';
// Archived dashboard components - keeping imports for backward compatibility
// import NewDashboard from './components/NewDashboard';
// import OldDashboard from './components/OldDashboard';
// import Dashboard_new from './components/Dashboard_new';
import './index.css';
import { BuildingDataService } from './services/buildingDataService';
import { Building } from './types/building';

// Wrapper component for Economic Optimizer with routing
const EconomicOptimizerWrapper: React.FC<{ buildings: Building[] }> = ({ buildings }) => {
    const { buildingId } = useParams<{ buildingId: string }>();
    const building = buildings.find(b => b.id === buildingId) || buildings[0];

    if (!building) {
        return <div className="text-center py-12">Building not found</div>;
    }

    return (
        <EconomicOptimizer
            building={building}
            onBack={() => window.history.back()}
        />
    );
};

const App: React.FC = () => {
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load NYC building data once when app starts
    useEffect(() => {
        const loadNYCData = async () => {
            setLoading(true);
            setError(null);

            try {
                console.log('🚀 Loading NYC Building Energy Data...');
                const startTime = performance.now();

                // Try to load the full dataset first
                let response = await fetch('/filtered-clean-data.csv');
                let dataSource = 'full dataset';

                // If full dataset fails, fallback to sample data
                if (!response.ok) {
                    console.log('⚠️ Full dataset not available, falling back to sample data...');
                    response = await fetch('/sample-data.csv');
                    dataSource = 'sample dataset';

                    if (!response.ok) {
                        throw new Error(`Failed to load sample data: HTTP ${response.status}`);
                    }
                }

                const csvText = await response.text();
                console.log(`📄 CSV loaded (${dataSource}): ${(csvText.length / 1024).toFixed(1)} KB`);

                const blob = new Blob([csvText], { type: 'text/csv' });
                const file = new File([blob], 'nyc-energy-data.csv', { type: 'text/csv' });

                console.log('🔄 Processing building data...');
                const buildingData = await BuildingDataService.parseCSVFile(file);

                const processingTime = ((performance.now() - startTime) / 1000).toFixed(2);
                console.log(`✅ Loaded ${buildingData.length.toLocaleString()} buildings (${dataSource} dataset) in ${processingTime}s`);
                console.log('Sample building from loaded data:', buildingData[0]);

                setBuildings(buildingData);

            } catch (err) {
                console.error('❌ Error loading NYC data:', err);
                setError(`Failed to load NYC building data: ${err instanceof Error ? err.message : 'Unknown error'}`);
            } finally {
                setLoading(false);
            }
        };

        loadNYCData();
    }, []);

    return (
        <Router>
            <div className="min-h-screen bg-gray-50">
                <Header />

                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <Routes>
                        {/* Homepage Route */}
                        <Route
                            path="/"
                            element={<Homepage />}
                        />

                        {/* New Website Pages */}
                        <Route path="/tools" element={<ToolsPage />} />
                        <Route path="/contact" element={<ContactUs />} />

                        {/* User-Specific Landing Pages */}
                        <Route path="/building-owners" element={<BuildingOwnersLanding />} />
                        <Route path="/contractors" element={<ContractorsLanding />} />
                        <Route path="/lenders" element={<LendersLanding />} />

                        {/* Legacy Pitch page for internal reference */}
                        <Route
                            path="/pitch"
                            element={
                                <Pitch
                                    buildings={buildings}
                                    loading={loading}
                                    error={error}
                                />
                            }
                        />

                        {/* Building Analysis Tools */}
                        <Route
                            path="/building-analysis"
                            element={
                                <BuildingDetailPage
                                    buildings={buildings}
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                        <Route
                            path="/building-analysis/:buildingId"
                            element={
                                <BuildingDetailPage
                                    buildings={buildings}
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                        <Route
                            path="/simple-view"
                            element={
                                <SimpleBuildingView
                                    buildings={buildings}
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                        <Route
                            path="/simple-view/:buildingId"
                            element={
                                <SimpleBuildingView
                                    buildings={buildings}
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                        <Route
                            path="/economic-optimizer/:buildingId"
                            element={
                                <EconomicOptimizerWrapper
                                    buildings={buildings}
                                />
                            }
                        />
                        <Route
                            path="/target-properties"
                            element={
                                <TargetPropertiesAnalysis
                                    buildings={buildings}
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                        <Route
                            path="/connection-hub"
                            element={
                                <ConnectionHub
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                        <Route
                            path="/company-management"
                            element={
                                <CompanyManagement onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/real-estate-partnerships"
                            element={
                                <RealEstatePartnerships onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/lender-partnerships"
                            element={
                                <LenderPartnerships onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/contractor-partnerships"
                            element={
                                <ContractorPartnerships onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/cto-recruitment"
                            element={
                                <CTORecruitment onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/staff-hiring"
                            element={
                                <StaffHiring onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/chinese-building-owners"
                            element={
                                <ChineseBuildingOwners onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/pitch"
                            element={
                                <Pitch
                                    buildings={buildings}
                                    loading={loading}
                                    error={error}
                                />
                            }
                        />
                        <Route
                            path="/calculation-verification"
                            element={
                                <CalculationVerificationPage
                                    buildings={buildings}
                                    loading={loading}
                                    error={error}
                                />
                            }
                        />

                        {/* Hidden components now accessible via routing */}
                        <Route
                            path="/tenant-outreach"
                            element={
                                <TenantOutreachPage />
                            }
                        />
                        <Route
                            path="/contractor-partnership-signup"
                            element={
                                <ContractorPartnershipPage onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/lender-partnership-signup"
                            element={
                                <LenderPartnershipPage onBack={() => window.history.back()} />
                            }
                        />
                        <Route
                            path="/ll97-calculator"
                            element={
                                <LL97Calculator />
                            }
                        />
                        <Route
                            path="/retrofit-calculator"
                            element={
                                <PublicRetrofitCalculator
                                    buildings={buildings}
                                />
                            }
                        />
                        <Route
                            path="/internal-retrofit-calculator"
                            element={
                                <InternalRetrofitCalculator
                                    buildings={buildings}
                                />
                            }
                        />
                        <Route
                            path="/definitions-tam"
                            element={
                                <DefinitionsAndTAM />
                            }
                        />


                        <Route
                            path="/economic-optimizer/:buildingId"
                            element={
                                <EconomicOptimizerWrapper
                                    buildings={buildings}
                                />
                            }
                        />
                        {/* Archived duplicate dashboard routes - cleaned up for production */}
                        {/* 
                        <Route 
                            path="/new-dashboard" 
                            element={
                                userType === 'internal' ? 
                                <NewDashboard 
                                    buildings={buildings}
                                    loading={loading}
                                    error={error}
                                /> :
                                <AccessDenied onBack={() => window.history.back()} />
                            } 
                        />
                        <Route 
                            path="/old-dashboard" 
                            element={
                                userType === 'internal' ? 
                                <OldDashboard 
                                    buildings={buildings}
                                    loading={loading}
                                    error={error}
                                /> :
                                <AccessDenied onBack={() => window.history.back()} />
                            } 
                        />
                        <Route 
                            path="/dashboard-new" 
                            element={
                                userType === 'internal' ? 
                                <Dashboard_new 
                                    buildings={buildings}
                                    loading={loading}
                                    error={error}
                                /> :
                                <AccessDenied onBack={() => window.history.back()} />
                            } 
                        />
                        */}
                        <Route
                            path="/non-compliant-buildings"
                            element={
                                <NonCompliantBuildingsDirectory
                                    onBack={() => window.history.back()}
                                />
                            }
                        />
                    </Routes>
                </main>

                <Footer />
            </div>
        </Router>
    );
};

interface HeaderProps {
    // No props needed - simplified header
}

const Header: React.FC<HeaderProps> = () => {
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState<string>('');

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setShowDropdown('');
        if (showDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showDropdown]);

    return (
        <header className="bg-white shadow-lg border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center py-4">
                    <div className="flex items-center space-x-4">
                        <Link to="/" className="flex items-center space-x-3 hover:opacity-90 transition-all duration-200">
                            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
                                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 9.739s9-4.189 9-9.739V7l-10-5z" />
                                    <path d="M12 8v8m-4-4h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </div>
                            <span className="text-2xl font-bold text-gray-900">
                                NYC Retrofit Analyzer
                            </span>
                        </Link>
                    </div>
                    <div className="flex items-center space-x-6">
                        <nav className="hidden md:flex items-center space-x-1">
                            {/* Primary CTA - Home */}
                            <Link
                                to="/"
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${location.pathname === '/'
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                                    }`}
                            >
                                Home
                            </Link>

                            {/* Core Tools */}
                            <div className="relative">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDropdown(showDropdown === 'tools' ? '' : 'tools');
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${['/ll97-calculator', '/retrofit-calculator', '/building-analysis', '/simple-view'].includes(location.pathname)
                                            ? 'bg-green-600 text-white'
                                            : 'text-gray-700 hover:text-green-600 hover:bg-green-50'
                                        }`}
                                >
                                    <span>Tools</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showDropdown === 'tools' && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                                        <div className="py-2">
                                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Calculators</div>
                                            <Link
                                                to="/ll97-calculator"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-green-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>💰</span><span>LL97 Fine Calculator</span>
                                            </Link>
                                            <Link
                                                to="/retrofit-calculator"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-green-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>🔧</span><span>Retrofit Calculator</span>
                                            </Link>
                                            <div className="border-t border-gray-200 mt-2 pt-2">
                                                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Analysis</div>
                                                <Link
                                                    to="/building-analysis"
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-green-600 flex items-center space-x-3"
                                                    onClick={() => setShowDropdown('')}
                                                >
                                                    <span>🏢</span><span>Building Analysis</span>
                                                </Link>
                                                <Link
                                                    to="/simple-view"
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-green-600 flex items-center space-x-3"
                                                    onClick={() => setShowDropdown('')}
                                                >
                                                    <span>📋</span><span>Simple View</span>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Services */}
                            <div className="relative">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDropdown(showDropdown === 'services' ? '' : 'services');
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${['/building-owners', '/contractors', '/lenders'].includes(location.pathname)
                                            ? 'bg-purple-600 text-white'
                                            : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                                        }`}
                                >
                                    <span>Services</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showDropdown === 'services' && (
                                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                                        <div className="py-2">
                                            <Link
                                                to="/building-owners"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-purple-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>🏢</span><span>Building Owners</span>
                                            </Link>
                                            <Link
                                                to="/contractors"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-purple-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>🔨</span><span>Contractors</span>
                                            </Link>
                                            <Link
                                                to="/lenders"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-purple-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>🏦</span><span>Lenders</span>
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Partnership */}
                            <div className="relative">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDropdown(showDropdown === 'partner' ? '' : 'partner');
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${['/contractor-partnership-signup', '/lender-partnership-signup'].includes(location.pathname)
                                            ? 'bg-orange-600 text-white'
                                            : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                                        }`}
                                >
                                    <span>Partner</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showDropdown === 'partner' && (
                                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                                        <div className="py-2">
                                            <Link
                                                to="/contractor-partnership-signup"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-orange-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>🔨</span><span>Join as Contractor</span>
                                            </Link>
                                            <Link
                                                to="/lender-partnership-signup"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-orange-600 flex items-center space-x-3"
                                                onClick={() => setShowDropdown('')}
                                            >
                                                <span>🏦</span><span>Join as Lender</span>
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>


                            {/* Chinese */}
                            <Link
                                to="/chinese-building-owners"
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${location.pathname === '/chinese-building-owners'
                                        ? 'bg-red-600 text-white'
                                        : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                                    }`}
                            >
                                <span>🇨🇳</span>
                                <span>中文</span>
                            </Link>

                            {/* Contact */}
                            <Link
                                to="/contact"
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200"
                            >
                                Contact
                            </Link>
                        </nav>
                    </div>
                </div>
            </div>
        </header>
    );
};



const Footer: React.FC = () => {
    return (
        <footer className="bg-gray-800 text-white py-8 mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <p className="text-sm text-gray-300">
                        NYC Building Energy Retrofit Analysis Tool | Identify cost-effective retrofits to reduce LL97 fines
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                        Data analysis tool for Local Law 97 compliance and retrofit opportunity assessment
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default App;